// BookingStep2.jsx
// Layout:
//   Left  → Selected car card (image, specs, price, Modify + Car Details buttons)
//           Car Details modal (specs, features, pricing, policy)
//           Quick rental summary below card
//   Right → Booking engine (pickup, dropoff, dates+times, driver age, days badge, search btn)
// Receives state from OurCars.jsx: { car_id, car_name, group_id, pickup,
//   dropoff, pickupDate, returnDate, pickupTime, returnTime, driverAge, diffDrop }

import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Navbar from "../components/Navbar";
import { API, BASE_URL } from "../api";
// const API_BASE = "http://localhost:5000/api";
// const API_BASE = "http://192.168.0.57:5000/api";
// ─── CSS ──────────────────────────────────────────────────────
const css = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700;900&family=DM+Sans:wght@300;400;500;600&display=swap');

  :root {
    --gold: #C9A84C;
    --gold-light: #E8C97A;
    --dark: #0A0A0F;
    --dark2: #111118;
    --dark3: #1A1A24;
    --surface: #16161F;
    --border: rgba(201,168,76,0.18);
    --text: #E8E8F0;
    --muted: #7A7A9A;
    --white: #FFFFFF;
    --green: #1D9E75;
    --red: #E05252;
    --radius: 12px;
  }

  * { margin:0; padding:0; box-sizing:border-box; }
  html { scroll-behavior:smooth; }
  body { font-family:'DM Sans',sans-serif; background:var(--dark); color:var(--text); }

  /* ── NAV ── */
  .s2-nav {
    position:fixed; top:0; left:0; right:0; z-index:1000;
    display:flex; align-items:center; justify-content:space-between;
    padding:0 5%; height:72px;
    background:rgba(10,10,15,0.96); backdrop-filter:blur(18px);
    border-bottom:1px solid var(--border);
  }
  .s2-logo { font-family:'Playfair Display',serif; font-size:1.6rem; color:var(--gold); cursor:pointer; }
  .s2-logo span { color:var(--white); }
  .s2-nav-links { display:flex; gap:2rem; list-style:none; }
  .s2-nav-links a { color:var(--muted); text-decoration:none; font-size:.9rem; font-weight:500; transition:color .2s; }
  .s2-nav-links a:hover { color:var(--gold); }

  /* ── STEPPER ── */
  .s2-stepper-wrap {
    padding-top:72px;
    background:var(--dark2);
    border-bottom:1px solid var(--border);
  }
  .s2-stepper {
    display:flex; align-items:flex-start; justify-content:center;
    padding:2rem 5%; gap:0; overflow-x:auto;
  }
  .s2-step { display:flex; flex-direction:column; align-items:center; flex:1; min-width:100px; position:relative; }
  .s2-step:not(:last-child)::after {
    content:''; position:absolute;
    top:20px; left:calc(50% + 20px); right:calc(-50% + 20px);
    height:2px; background:var(--border); z-index:0;
  }
  .s2-step.done:not(:last-child)::after,
  .s2-step.active:not(:last-child)::after { background:var(--gold); }
  .s2-step-circle {
    width:40px; height:40px; border-radius:50%;
    border:2px solid var(--border); background:var(--surface);
    display:flex; align-items:center; justify-content:center;
    font-size:.85rem; font-weight:700; color:var(--muted);
    position:relative; z-index:1; flex-shrink:0; transition:all .3s;
  }
  .s2-step.done .s2-step-circle  { background:var(--gold); border-color:var(--gold); color:var(--dark); }
  .s2-step.active .s2-step-circle { border-color:var(--gold); color:var(--gold); box-shadow:0 0 0 4px rgba(201,168,76,.18); }
  .s2-step-label { font-size:.72rem; color:var(--muted); margin-top:.6rem; text-align:center; font-weight:500; letter-spacing:.3px; }
  .s2-step.active .s2-step-label { color:var(--gold); font-weight:600; }
  .s2-step.done  .s2-step-label  { color:var(--text); }

  /* ── PAGE BODY ── */
  .s2-body { display:flex; gap:2rem; padding:2.5rem 5% 5rem; align-items:flex-start; }

  /* ── LEFT PANEL ── */
  .s2-left { width:290px; flex-shrink:0; position:sticky; top:88px; align-self:flex-start; }

  .s2-car-card {
    background:var(--surface); border:1px solid var(--border);
    border-radius:16px; overflow:hidden;
  }
  .s2-car-img  { width:100%; height:175px; object-fit:cover; display:block; }
  .s2-car-body { padding:1.2rem; }
  .s2-car-group { font-size:.66rem; color:var(--gold); font-weight:700; letter-spacing:1.2px; text-transform:uppercase; margin-bottom:.25rem; }
  .s2-car-name  { font-family:'Playfair Display',serif; font-size:1.15rem; color:var(--white); margin-bottom:.85rem; line-height:1.3; }
  .s2-car-specs { display:grid; grid-template-columns:1fr 1fr; gap:.4rem; margin-bottom:1rem; }
  .s2-spec      { font-size:.76rem; color:var(--muted); display:flex; align-items:center; gap:.35rem; }

  /* Price tag */
  .s2-price-tag {
    background:var(--dark3); border:1px solid var(--border); border-radius:10px;
    padding:.7rem 1rem; margin-bottom:1rem;
    display:flex; justify-content:space-between; align-items:center;
  }
  .s2-pt-label { font-size:.68rem; color:var(--muted); letter-spacing:.5px; }
  .s2-pt-sub   { font-size:.64rem; color:var(--muted); }
  .s2-pt-val   { font-family:'Playfair Display',serif; font-size:1.3rem; color:var(--gold); font-weight:700; }

  /* Action buttons */
  .s2-action-row { display:flex; gap:.6rem; }
  .s2-btn-modify {
    flex:1; background:transparent; border:1px solid var(--gold); color:var(--gold);
    font-size:.78rem; font-weight:600; padding:.58rem .8rem; border-radius:8px;
    cursor:pointer; font-family:inherit; transition:all .2s;
  }
  .s2-btn-modify:hover { background:var(--gold); color:var(--dark); }
  .s2-btn-details {
    flex:1; background:var(--gold); border:none; color:var(--dark);
    font-size:.78rem; font-weight:700; padding:.58rem .8rem; border-radius:8px;
    cursor:pointer; font-family:inherit; transition:all .2s;
  }
  .s2-btn-details:hover { background:var(--gold-light); }

  /* Quick rental summary */
  .s2-quick-card {
    background:var(--surface); border:1px solid var(--border); border-radius:14px;
    padding:1.1rem; margin-top:1rem;
  }
  .s2-qc-title {
    font-size:.66rem; letter-spacing:1.5px; text-transform:uppercase;
    color:var(--gold); font-weight:600; margin-bottom:.8rem;
    padding-bottom:.6rem; border-bottom:1px solid var(--border);
  }
  .s2-qc-row {
    display:flex; justify-content:space-between; align-items:center;
    padding:.38rem 0; border-bottom:1px solid var(--border); font-size:.78rem;
  }
  .s2-qc-row:last-child { border-bottom:none; }
  .s2-qc-lbl { color:var(--muted); }
  .s2-qc-val { color:var(--text); font-weight:500; text-align:right; }

  /* ── RIGHT PANEL — BOOKING ENGINE ── */
  .s2-right { flex:1; min-width:0; }
  .s2-engine-card {
    background:var(--surface); border:1px solid var(--border);
    border-radius:16px; padding:1.8rem;
  }
  .s2-engine-title {
    font-family:'Playfair Display',serif; font-size:1.35rem; color:var(--white);
    margin-bottom:1.4rem; padding-bottom:.9rem; border-bottom:1px solid var(--border);
    display:flex; align-items:center; gap:.6rem;
  }

  .s2-field-label {
    font-size:.66rem; letter-spacing:1.5px; text-transform:uppercase;
    color:var(--gold); font-weight:600; display:block; margin-bottom:.4rem;
  }
  .s2-e-input, .s2-e-select {
    width:100%; background:rgba(255,255,255,.05); border:1px solid var(--border);
    color:var(--text); padding:.7rem .95rem; border-radius:8px;
    font-size:.87rem; font-family:inherit; outline:none; transition:border .2s;
  }
  .s2-e-input:focus, .s2-e-select:focus { border-color:var(--gold); }
  .s2-e-input::placeholder { color:var(--muted); }
  .s2-e-select { cursor:pointer; appearance:none; }
  select option { background:#1a1a24; }

  .s2-row2 { display:grid; grid-template-columns:1fr 1fr; gap:1rem; margin-bottom:1rem; }
  .s2-row1 { margin-bottom:1rem; }
  .s2-dt-pair { display:flex; gap:.5rem; }
  .s2-dt-pair .s2-e-input  { flex:2; }
  .s2-dt-pair .s2-e-select { flex:1; }

  /* Diff-drop checkbox */
  .s2-diff-row {
    display:flex; align-items:center; gap:.4rem;
    margin-top:.45rem; font-size:.73rem; color:var(--muted); cursor:pointer;
  }
  .s2-diff-row input { accent-color:var(--gold); width:13px; height:13px; cursor:pointer; }

  /* Days badge */
  .s2-days-badge {
    background:rgba(201,168,76,.12); border:1px solid var(--gold); border-radius:8px;
    padding:.68rem 1rem; font-size:.84rem; color:var(--gold); font-weight:700;
    white-space:nowrap; text-align:center;
    display:flex; align-items:center; justify-content:center; align-self:flex-end;
  }

  /* Young driver warning */
  .s2-warn { font-size:.69rem; color:#EF9F27; margin-top:.3rem; display:block; }

  /* Divider */
  .s2-divider { border:none; border-top:1px solid var(--border); margin:1.3rem 0; }

  /* Search button */
  .s2-search-btn {
    width:100%; background:var(--gold); color:var(--dark); font-weight:700;
    padding:.85rem; border:none; border-radius:10px; font-size:.97rem;
    cursor:pointer; font-family:inherit; transition:all .25s; letter-spacing:.3px;
  }
  .s2-search-btn:hover { background:var(--gold-light); transform:translateY(-2px); box-shadow:0 12px 35px rgba(201,168,76,.28); }

  /* Summary strip tags */
  .s2-strip { display:flex; gap:.5rem; flex-wrap:wrap; margin-top:1rem; }
  .s2-tag {
    background:var(--dark3); border:1px solid var(--border); border-radius:6px;
    padding:.3rem .7rem; font-size:.72rem; color:var(--muted);
  }
  .s2-tag strong { color:var(--text); }

  /* ── CAR DETAILS MODAL ── */
  .s2-modal-overlay {
    display:none; position:fixed; inset:0; background:rgba(0,0,0,.78);
    z-index:9999; align-items:center; justify-content:center; padding:1rem;
  }
  .s2-modal-overlay.open { display:flex; }
  .s2-modal {
    background:var(--dark2); border:1px solid var(--border); border-radius:20px;
    width:100%; max-width:540px; max-height:88vh; overflow-y:auto;
    animation:s2slide .25s ease;
  }
  @keyframes s2slide { from { opacity:0; transform:translateY(18px); } to { opacity:1; transform:none; } }
  .s2-modal-head {
    display:flex; justify-content:space-between; align-items:center;
    padding:1.3rem 1.5rem; border-bottom:1px solid var(--border);
    position:sticky; top:0; background:var(--dark2); z-index:2;
    border-radius:20px 20px 0 0;
  }
  .s2-modal-head h2 { font-family:'Playfair Display',serif; font-size:1.25rem; color:var(--white); }
  .s2-modal-close {
    background:none; border:1px solid var(--border); color:var(--muted);
    width:32px; height:32px; border-radius:50%; cursor:pointer; font-size:1rem;
    display:flex; align-items:center; justify-content:center; transition:all .2s;
  }
  .s2-modal-close:hover { border-color:var(--gold); color:var(--gold); }
  .s2-modal-body { padding:1.5rem; }
  .s2-modal-car-img { width:100%; height:200px; object-fit:cover; border-radius:12px; margin-bottom:1.2rem; }

  .s2-modal-sec { margin-bottom:1.3rem; }
  .s2-modal-sec-title {
    font-size:.66rem; letter-spacing:1.5px; text-transform:uppercase;
    color:var(--gold); font-weight:600; margin-bottom:.75rem;
    padding-bottom:.5rem; border-bottom:1px solid var(--border);
  }
  .s2-modal-specs-grid { display:grid; grid-template-columns:1fr 1fr; gap:.6rem; }
  .s2-modal-spec {
    background:var(--dark3); border:1px solid var(--border);
    border-radius:8px; padding:.65rem .9rem;
  }
  .s2-modal-spec-lbl { font-size:.65rem; color:var(--muted); letter-spacing:.5px; margin-bottom:.15rem; text-transform:uppercase; }
  .s2-modal-spec-val { font-size:.92rem; color:var(--white); font-weight:600; }

  .s2-features { display:flex; flex-wrap:wrap; gap:.5rem; }
  .s2-feature-pill {
    background:rgba(201,168,76,.1); border:1px solid rgba(201,168,76,.25);
    border-radius:20px; padding:.3rem .85rem; font-size:.75rem; color:var(--gold);
  }

  .s2-modal-price-row {
    display:flex; justify-content:space-between;
    padding:.42rem 0; font-size:.84rem; border-bottom:1px solid var(--border);
  }
  .s2-modal-price-row:last-child { border-bottom:none; }
  .s2-modal-price-row span:first-child { color:var(--muted); }
  .s2-modal-price-row span:last-child  { color:var(--text); font-weight:500; }

  .s2-btn-keep {
    width:100%; background:var(--gold); color:var(--dark); font-weight:700;
    padding:.82rem; border:none; border-radius:10px; font-size:.92rem;
    cursor:pointer; font-family:inherit; margin-top:1rem; transition:all .2s;
  }
  .s2-btn-keep:hover { background:var(--gold-light); }

  /* ── RESPONSIVE ── */
  @media (max-width:900px) {
    .s2-body { flex-direction:column; }
    .s2-left { width:100%; position:static; }
  }
  @media (max-width:768px) {
    .s2-nav-links { display:none; }
    .s2-row2 { grid-template-columns:1fr; }
    .s2-body { padding:2rem 4% 4rem; }
    .s2-engine-card { padding:1.3rem; }
    .s2-step-label { font-size:.65rem; }
  }
  @media (max-width:580px) {
    .s2-step-label { display:none; }
    .s2-step-circle { width:32px; height:32px; font-size:.75rem; }
    .s2-step:not(:last-child)::after { top:16px; }
  }
`;

// ─── TIME OPTIONS ─────────────────────────────────────────────
const TIME_OPTIONS = [
  "00:00","01:00","02:00","03:00","04:00","05:00",
  "06:00","07:00","08:00","09:00","10:00","11:00",
  "12:00","13:00","14:00","15:00","16:00","17:00",
  "18:00","19:00","20:00","21:00","22:00","23:00",
];

// ─── STEPPER ─────────────────────────────────────────────────
const STEPS = [
  { label: "Choose Location" },
  { label: "Choose Your Car" },
  { label: "Reserve Car" },
  { label: "Payment" },
  { label: "Confirmation" },
];

// ─── HELPERS ─────────────────────────────────────────────────
const getImgUrl = (img) => {
  if (!img || img === "default.jpg")
    return "https://images.unsplash.com/photo-1503376780353-7e6692767b70?w=800&q=80";
  if (img.startsWith("http")) return img;
  // return `${API_BASE.replace("/api", "")}/${img}`;
  return `${BASE_URL}/${img}`;
};

const fmtDate = (d) => {
  if (!d) return "—";
  const [y, m, day] = d.split("-");
  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  return `${day} ${months[Number(m) - 1]} ${y}`;
};

const calcDays = (from, to) => {
  if (!from || !to) return 0;
  const diff = (new Date(to) - new Date(from)) / 86400000;
  return diff > 0 ? Math.round(diff) : 0;
};

const today = new Date().toISOString().split("T")[0];

// ─── NAVBAR ──────────────────────────────────────────────────
// function S2Navbar({ siteName }) {
//   const navigate = useNavigate();
//   return (
//     <nav className="s2-nav">
//       <div className="s2-logo" onClick={() => navigate("/")}>
//         Orisnz<span>{siteName?.replace("Drive", "") || "-Fleet"}</span>
//       </div>
//       <ul className="s2-nav-links">
//         {["Home", "Fleet", "About", "Contact"].map((l) => (
//           <li key={l}><a href={l === "Home" ? "/" : "#"}>{l}</a></li>
//         ))}
//       </ul>
//     </nav>
//   );
// }

// ─── STEPPER ─────────────────────────────────────────────────
function Stepper({ current }) {
  return (
    <div className="s2-stepper-wrap">
      <div className="s2-stepper">
        {STEPS.map((s, i) => {
          const state = i < current ? "done" : i === current ? "active" : "";
          return (
            <div key={s.label} className={`s2-step ${state}`}>
              <div className="s2-step-circle">{i < current ? "✓" : i + 1}</div>
              <div className="s2-step-label">{s.label}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── CAR DETAILS MODAL ───────────────────────────────────────
function CarDetailsModal({ open, onClose, car, currency }) {
  if (!open) return null;

  const price     = car?.base_price || car?.price || 0;
  const weekRate  = Math.round(price * 0.85);
  const monthRate = Math.round(price * 0.72);
  const deposit   = Math.round(price * 5.5);

  return (
    <div className={`s2-modal-overlay${open ? " open" : ""}`} onClick={(e) => { if (e.target.className.includes("s2-modal-overlay")) onClose(); }}>
      <div className="s2-modal">
        {/* Head */}
        <div className="s2-modal-head">
          <h2>Car Details</h2>
          <button className="s2-modal-close" onClick={onClose}>✕</button>
        </div>

        <div className="s2-modal-body">
          {/* Car image */}
          <img
            className="s2-modal-car-img"
            src={getImgUrl(car?.car_image)}
            alt={car?.car_name || "Car"}
          />

          {/* Name / group */}
          <div style={{ marginBottom: "1.2rem" }}>
            <div className="s2-car-group">{car?.group_name || car?.cat_name || "Car"}</div>
            <div style={{ fontFamily: "'Playfair Display',serif", fontSize: "1.35rem", color: "var(--white)", marginBottom: ".25rem" }}>
              {car?.car_name || "Your Selected Car"}
            </div>
            <div style={{ fontSize: ".78rem", color: "var(--muted)" }}>or similar vehicle from this category</div>
          </div>

          {/* Specifications */}
          <div className="s2-modal-sec">
            <div className="s2-modal-sec-title">Specifications</div>
            <div className="s2-modal-specs-grid">
              {[
                ["Passengers", car?.car_passgrno ? `${car.car_passgrno} Seats` : "—"],
                ["Luggage",    car?.car_luggno   ? `${car.car_luggno} Bags`   : "—"],
                ["Transmission", car?.car_transmission || "—"],
                ["Doors",      car?.car_doors ? `${car.car_doors} Doors` : "—"],
                ["Engine",     car?.car_engcapacity > 0 ? `${car.car_engcapacity}cc` : "—"],
                ["Air Con",    car?.car_airconditionr === "Yes" ? "Yes" : "No"],
              ].map(([lbl, val]) => (
                <div key={lbl} className="s2-modal-spec">
                  <div className="s2-modal-spec-lbl">{lbl}</div>
                  <div className="s2-modal-spec-val">{val}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Included features */}
          <div className="s2-modal-sec">
            <div className="s2-modal-sec-title">Included Features</div>
            <div className="s2-features">
              {["❄️ Air Conditioning", "📻 Bluetooth Audio", "🔋 USB Charging",
                "🛡 Basic Insurance", "📍 Navigation", "🚗 Cruise Control"].map((f) => (
                <span key={f} className="s2-feature-pill">{f}</span>
              ))}
            </div>
          </div>

          {/* Pricing */}
          <div className="s2-modal-sec">
            <div className="s2-modal-sec-title">Pricing</div>
            <div className="s2-modal-price-row">
              <span>Daily Rate</span>
              <span style={{ color: "var(--gold)", fontWeight: 700 }}>{currency} {price.toLocaleString()} / day</span>
            </div>
            <div className="s2-modal-price-row">
              <span>Weekly Rate (7+ days)</span>
              <span>{currency} {weekRate.toLocaleString()} / day</span>
            </div>
            <div className="s2-modal-price-row">
              <span>Monthly Rate (30+ days)</span>
              <span>{currency} {monthRate.toLocaleString()} / day</span>
            </div>
            <div className="s2-modal-price-row">
              <span>Security Deposit</span>
              <span>{currency} {deposit.toLocaleString()} (refundable)</span>
            </div>
          </div>

          {/* Policy */}
          <div className="s2-modal-sec">
            <div className="s2-modal-sec-title">Rental Policy</div>
            <div style={{ fontSize: ".8rem", color: "var(--muted)", lineHeight: 1.75 }}>
              Minimum rental: 1 day. Fuel policy: Full-to-Full. Mileage: Unlimited.
              Minimum driver age: 21 years. Valid driving licence required at pickup.
              Vehicle delivered clean and in good condition.
            </div>
          </div>

          <button className="s2-btn-keep" onClick={onClose}>✓ Keep This Car &amp; Continue</button>
        </div>
      </div>
    </div>
  );
}

// ─── LEFT PANEL ──────────────────────────────────────────────
function LeftPanel({ car, searchState, currency, days, onOpenModal }) {
  const navigate = useNavigate();
  // base_price is the normalised field — car.price is the OurCars fallback
  const price = car?.base_price || car?.price || 0;
  const estTotal = price * days;

  return (
    <div className="s2-left">
      {/* Car card */}
      <div className="s2-car-card">
        <img
          className="s2-car-img"
          src={getImgUrl(car?.car_image)}
          alt={car?.car_name || "Selected Car"}
        />
        <div className="s2-car-body">
          <div className="s2-car-group">{car?.group_name || car?.cat_name || "Car"}</div>
          <div className="s2-car-name">{car?.car_name || "Your Selected Car"}</div>

          {/* Specs */}
          <div className="s2-car-specs">
            {car?.car_passgrno   && <div className="s2-spec">👥 {car.car_passgrno} Seats</div>}
            {car?.car_luggno     && <div className="s2-spec">🧳 {car.car_luggno} Bags</div>}
            {car?.car_transmission && <div className="s2-spec">⚙️ {car.car_transmission}</div>}
            {car?.car_airconditionr === "Yes" && <div className="s2-spec">❄️ A/C</div>}
            {car?.car_doors      && <div className="s2-spec">🚪 {car.car_doors} Doors</div>}
            {car?.car_engcapacity > 0 && <div className="s2-spec">🔧 {car.car_engcapacity}cc</div>}
          </div>

          {/* Price tag */}
          {price > 0 && (
            <div className="s2-price-tag">
              <div>
                <div className="s2-pt-label">Daily Rate</div>
                <div className="s2-pt-sub">excl. extras</div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div className="s2-pt-val">{currency} {price.toLocaleString()}</div>
                <div className="s2-pt-sub">/ day</div>
              </div>
            </div>
          )}

          {/* Modify + Details buttons */}
          <div className="s2-action-row">
            <button className="s2-btn-modify" onClick={() => navigate(-1)}>← Modify</button>
            <button className="s2-btn-details" onClick={onOpenModal}>Car Details</button>
          </div>
        </div>
      </div>

      {/* Quick rental summary */}
      {/* <div className="s2-quick-card">
        <div className="s2-qc-title">Current Selection</div>
        <div className="s2-qc-row">
          <span className="s2-qc-lbl">📍 Pickup</span>
          <span className="s2-qc-val">{searchState.pickup || "—"}</span>
        </div>
        {searchState.dropoff && searchState.dropoff !== searchState.pickup && (
          <div className="s2-qc-row">
            <span className="s2-qc-lbl">🏁 Drop-off</span>
            <span className="s2-qc-val">{searchState.dropoff}</span>
          </div>
        )}
        <div className="s2-qc-row">
          <span className="s2-qc-lbl">📅 From</span>
          <span className="s2-qc-val">{fmtDate(searchState.pickupDate)}</span>
        </div>
        <div className="s2-qc-row">
          <span className="s2-qc-lbl">📅 To</span>
          <span className="s2-qc-val">{fmtDate(searchState.returnDate)}</span>
        </div>
        <div className="s2-qc-row">
          <span className="s2-qc-lbl">🗓 Duration</span>
          <span className="s2-qc-val" style={{ color: "var(--gold)", fontWeight: 700 }}>
            {days} day{days !== 1 ? "s" : ""}
          </span>
        </div>
        {estTotal > 0 && (
          <div className="s2-qc-row">
            <span className="s2-qc-lbl">💰 Est. Total</span>
            <span className="s2-qc-val" style={{ color: "var(--gold)", fontWeight: 700 }}>
              {currency} {estTotal.toLocaleString()}
            </span>
          </div>
        )}
      </div> */}
    </div>
  );
}

// ─── RIGHT PANEL — BOOKING ENGINE ────────────────────────────
function BookingEngine({ searchState, onSearch, onProceed }) {
  const [pickup,      setPickup]      = useState(searchState.pickup      || "");
  const [dropoff,     setDropoff]     = useState(searchState.dropoff     || "");
  const [diffDrop,    setDiffDrop]    = useState(searchState.diffDrop    || false);
  const [pickupDate,  setPickupDate]  = useState(searchState.pickupDate  || "");
  const [returnDate,  setReturnDate]  = useState(searchState.returnDate  || "");
  const [pickupTime,  setPickupTime]  = useState(searchState.pickupTime  || "09:00");
  const [returnTime,  setReturnTime]  = useState(searchState.returnTime  || "09:00");
  const [driverAge,   setDriverAge]   = useState(searchState.driverAge   || "");
  const [locations,   setLocations]   = useState([]);  

  
  useEffect(() => {
    // fetch(`${API_BASE}/locations`)
    //   .then(r => r.json())
    //   .then(d => {
    //     const list = Array.isArray(d) ? d : (Array.isArray(d?.data) ? d.data : []);
    //     setLocations(list);
    //   })
    //   .catch(() => setLocations([]));
    API.get("/locations")
  .then(r => {
    const list = Array.isArray(r.data) ? r.data : (Array.isArray(r.data?.data) ? r.data.data : []);
    setLocations(list);
  })
  .catch(() => setLocations([]));
  }, []);
  const days = calcDays(pickupDate, returnDate);

  const handlePickupDate = (val) => {
    setPickupDate(val);
    if (!returnDate || returnDate <= val) {
      const next = new Date(val);
      next.setDate(next.getDate() + 1);
      setReturnDate(next.toISOString().split("T")[0]);
    }
  };

  // Shared params builder used by both buttons
  const buildParams = () => ({
    pickup,
    dropoff: diffDrop ? dropoff : pickup,
    diffDrop,
    pickupDate,
    returnDate,
    pickupTime,
    returnTime,
    driverAge,
  });

  const validate = () => {
    if (!pickup || !pickupDate || !returnDate || !driverAge) {
      alert("Please select a pickup location, dates and driver age.");
      return false;
    }
    return true;
  };

  const handleSearch  = () => { if (validate()) onSearch(buildParams()); };
  const handleProceed = () => { if (validate()) onProceed(buildParams()); };

  return (
    <div className="s2-right">
      <div className="s2-engine-card">
        <div className="s2-engine-title">
          <span>📍</span> Modify Booking Details
        </div>

        {/* Pickup Location */}
        <div className="s2-row1">
          <label className="s2-field-label">Pickup Location</label>
          <select
          className="s2-e-select"
          value={pickup}
          onChange={(e) => setPickup(e.target.value)}
          >
          <option value="">Select pickup location…</option>
          {locations.map(loc => (
          <option key={loc.loc_id} value={loc.loc_name}>
          {loc.loc_name}{loc.city_name ? ` — ${loc.city_name}` : ""}
          </option>
          ))}
          </select>

          <label className="s2-diff-row">
            <input
              type="checkbox"
              checked={diffDrop}
              onChange={(e) => setDiffDrop(e.target.checked)}
            />
            Drop off at a different location?
          </label>
        </div>

        {/* Drop-off Location */}
        {diffDrop && (
  <div className="s2-row1">
    <label className="s2-field-label">Drop-off Location</label>
    <select
      className="s2-e-select"
      value={dropoff}
      onChange={(e) => setDropoff(e.target.value)}
    >
      <option value="">Select drop-off location…</option>
      {locations.map(loc => (
        <option key={loc.loc_id} value={loc.loc_name}>
          {loc.loc_name}{loc.city_name ? ` — ${loc.city_name}` : ""}
        </option>
      ))}
    </select>
  </div>
)}

        {/* Dates */}
        <div className="s2-row2">
          <div>
            <label className="s2-field-label">Pickup Date &amp; Time</label>
            <div className="s2-dt-pair">
              <input
                className="s2-e-input"
                type="date"
                min={today}
                value={pickupDate}
                onChange={(e) => handlePickupDate(e.target.value)}
              />
              <select
                className="s2-e-select"
                value={pickupTime}
                onChange={(e) => setPickupTime(e.target.value)}
              >
                {TIME_OPTIONS.map((t) => <option key={t}>{t}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="s2-field-label">Return Date &amp; Time</label>
            <div className="s2-dt-pair">
              <input
                className="s2-e-input"
                type="date"
                min={pickupDate || today}
                value={returnDate}
                onChange={(e) => setReturnDate(e.target.value)}
              />
              <select
                className="s2-e-select"
                value={returnTime}
                onChange={(e) => setReturnTime(e.target.value)}
              >
                {TIME_OPTIONS.map((t) => <option key={t}>{t}</option>)}
              </select>
            </div>
          </div>
        </div>

        {/* Driver Age + Days Badge */}
        <div className="s2-row2" style={{ alignItems: "flex-end" }}>
          <div>
            <label className="s2-field-label">Driver Age (Min 21)</label>
            <select
              className="s2-e-select"
              value={driverAge}
              onChange={(e) => setDriverAge(e.target.value)}
            >
              <option value="">Select age range</option>
              <option value="21-25">21 – 25 yrs</option>
              <option value="26-35">26 – 35 yrs</option>
              <option value="36-45">36 – 45 yrs</option>
              <option value="46-60">46 – 60 yrs</option>
              <option value="61-70">61 – 70 yrs</option>
              <option value="71+">71+ yrs</option>
            </select>
            {driverAge === "21-25" && (
              <span className="s2-warn">⚠️ Young driver surcharge may apply</span>
            )}
          </div>
          {days > 0 && (
            <div className="s2-days-badge">
              {days} day{days !== 1 ? "s" : ""}
            </div>
          )}
        </div>

        <hr className="s2-divider" />

        {/* Search Button */}
        {/* <button className="s2-search-btn" onClick={handleSearch}>
          Update &amp; Search Available Cars →
        </button> */}
        <button className="s2-search-btn" onClick={handleProceed}>
        Continue to Reservation →
        </button>

        {/* Continue to Step 3 */}
        {/* <button
          className="s2-search-btn"
          onClick={handleProceed}
          style={{
            marginTop: ".75rem",
            background: "var(--dark3)",
            color: "var(--gold)",
            border: "1.5px solid var(--gold)",
          }}
        >
          Continue to Reservation →
        </button> */}

        {/* Summary strip */}
        <div className="s2-strip">
          {pickup     && <div className="s2-tag">Pickup: <strong>{pickup}</strong></div>}
          {pickupDate && <div className="s2-tag">From: <strong>{fmtDate(pickupDate)}</strong></div>}
          {returnDate && <div className="s2-tag">To: <strong>{fmtDate(returnDate)}</strong></div>}
          {days > 0   && <div className="s2-tag">Duration: <strong>{days} day{days !== 1 ? "s" : ""}</strong></div>}
          {driverAge  && <div className="s2-tag">Age: <strong>{driverAge}</strong></div>}
        </div>
      </div>
    </div>
  );
}

// ─── MAIN EXPORT ─────────────────────────────────────────────
export default function BookingStep2() {
  const navigate    = useNavigate();
  const location    = useLocation();
  const searchState = location.state || {};

  const [settings,  setSettings]  = useState({ site_name: "Orisnz-Fleet", currency: "₹" });
  const [car,       setCar]       = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const currency = settings?.currency || "₹";

  // Load site settings
  useEffect(() => {
    // fetch(`${API_BASE}/settings`)
    //   .then((r) => r.json())
    //   .then((d) => { if (d && typeof d === "object") setSettings(d); })
    //   .catch(() => {});
    API.get("/settings")
  .then(r => { if (r.data && typeof r.data === "object") setSettings(r.data); })
  .catch(() => {});
  }, []);

  // Load car details and merge base_price from searchState.price
  // (because /api/cars/:id returns specs only — pricing comes from /api/price
  //  which OurCars already resolved and passed via navigate state)
  useEffect(() => {
    const id = searchState.car_id;
    if (!id) return;
    // fetch(`${API_BASE}/cars/${id}`)
    //   .then((r) => r.json())
    //   .then((d) => {
    //     const c = Array.isArray(d)
    //       ? d[0]
    //       : d?.data
    //       ? Array.isArray(d.data) ? d.data[0] : d.data
    //       : d;
    //     if (c) {
    //       // Normalise to base_price — this is what BookingStep3 reads
    //       setCar({
    //         ...c,
    //         base_price: c.base_price || c.price || searchState.price || 0,
    //       });
    //     }
    //   })
    //   .catch(() => {});
    API.get(`/cars/${id}`)
  .then(r => {
    const d = r.data;
    const c = Array.isArray(d)
      ? d[0]
      : d?.data
      ? Array.isArray(d.data) ? d.data[0] : d.data
      : d;
    if (c) {
      setCar({ ...c, base_price: c.base_price || c.price || searchState.price || 0 });
    }
  })
  .catch(() => {});
  }, [searchState.car_id]);

  const days = calcDays(searchState.pickupDate, searchState.returnDate);

  // displayCar: always has base_price so the price tag and modal work
  // even before the API fetch resolves
  const displayCar = car
    ? { ...car, base_price: car.base_price || searchState.price || 0 }
    : {
        car_id:         searchState.car_id,
        car_name:       searchState.car_name,
        car_brand:      searchState.car_brand   || "",
        cat_name:       searchState.cat_name    || searchState.group_name || "",
        group_name:     searchState.group_name  || "",
        car_passgrno:   searchState.car_passgrno || null,
        car_luggno:     searchState.car_luggno   || null,
        car_transmission: searchState.car_transmission || null,
        car_airconditionr: searchState.car_airconditionr || null,
        car_doors:      searchState.car_doors    || null,
        car_engcapacity: searchState.car_engcapacity || 0,
        car_image:      searchState.car_image    || null,
        base_price:     searchState.price        || 0,
      };

  // ── Navigate back to car search with updated params ──────────
  const handleSearch = (newParams) => {
    navigate("/our-cars", { state: { ...searchState, ...newParams } });
  };

  // ── Navigate to BookingStep3 with the exact shape Step3 expects ──
  // BookingStep3 reads:
  //   ctx.pickup, ctx.dropoff, ctx.pickupDate, ctx.pickupTime,
  //   ctx.returnDate, ctx.returnTime, ctx.driverAge, ctx.days,
  //   ctx.selectedCar.base_price, ctx.selectedCar.car_name, etc.
  //   ctx.selectedExtras, ctx.selectedInsurance
  const handleProceedToStep3 = (engineParams) => {
    const finalDays = calcDays(
      engineParams.pickupDate || searchState.pickupDate,
      engineParams.returnDate || searchState.returnDate
    );

    navigate("/booking-step3", {
      state: {
        // Search context (may have been updated in the engine)
        pickup:      engineParams.pickup      || searchState.pickup,
        dropoff:     engineParams.dropoff     || searchState.dropoff || searchState.pickup,
        pickupDate:  engineParams.pickupDate  || searchState.pickupDate,
        pickupTime:  engineParams.pickupTime  || searchState.pickupTime  || "09:00",
        returnDate:  engineParams.returnDate  || searchState.returnDate,
        returnTime:  engineParams.returnTime  || searchState.returnTime  || "09:00",
        driverAge:   engineParams.driverAge   || searchState.driverAge,
        days:        finalDays,

        // selectedCar — exact shape BookingStep3 uses
        selectedCar: {
          car_id:          displayCar.car_id,
          car_name:        displayCar.car_name,
          car_brand:       displayCar.car_brand       || "",
          cat_name:        displayCar.cat_name        || "",
          group_name:      displayCar.group_name      || "",
          car_passgrno:    displayCar.car_passgrno    || null,
          car_luggno:      displayCar.car_luggno      || null,
          car_transmission: displayCar.car_transmission || null,
          car_airconditionr: displayCar.car_airconditionr || null,
          car_doors:       displayCar.car_doors       || null,
          car_engcapacity: displayCar.car_engcapacity || 0,
          car_image:       displayCar.car_image       || null,
          base_price:      displayCar.base_price      || searchState.price || 0,
        },

        // Pre-filled defaults (Step3 will populate these)
        selectedExtras:    [],
        selectedInsurance: null,
      },
    });
  };

  return (
    <>
      <style>{css}</style>
      {/* <S2Navbar siteName={settings?.site_name} /> */}
      <Navbar activePage="Fleet" siteName={settings?.site_name} />
      <Stepper current={2} />

      <div className="s2-body">
        {/* LEFT */}
        <LeftPanel
          car={displayCar}
          searchState={searchState}
          currency={currency}
          days={days}
          onOpenModal={() => setModalOpen(true)}
        />

        {/* RIGHT */}
        <BookingEngine
          searchState={searchState}
          onSearch={handleSearch}
          onProceed={handleProceedToStep3}
        />
      </div>

      {/* CAR DETAILS MODAL */}
      <CarDetailsModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        car={displayCar}
        currency={currency}
      />
    </>
  );
}